import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import styles from './EventsEditor.module.css';

const EventsEditor = () => {
  const { hotelData, updateSection, loading } = useData();
  const [events, setEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editingEvent, setEditingEvent] = useState(null);

  useEffect(() => {
    if (hotelData?.events) {
      setEvents([...hotelData.events]);
    }
  }, [hotelData]);

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    const result = await updateSection('events', events);
    
    if (result.success) {
      setMessage({ type: 'success', text: 'Events updated successfully!' });
      setEditingEvent(null);
    } else {
      setMessage({ type: 'error', text: result.error || 'Failed to update events' });
    }

    setSaving(false);
    
    // Clear message after 3 seconds
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const handleReset = () => {
    if (hotelData?.events) {
      setEvents([...hotelData.events]);
      setEditingEvent(null);
      setMessage({ type: 'info', text: 'Changes reset to last saved version' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }
  };

  const handleEventUpdate = (index, field, value) => {
    const updated = [...events];
    updated[index] = { ...updated[index], [field]: value };
    setEvents(updated);
  };

  const handleToggleActive = (index) => {
    const updated = [...events];
    updated[index] = { ...updated[index], active: !updated[index].active };
    setEvents(updated);
  };

  const handleAddEvent = () => {
    const newEvent = {
      id: `event-${Date.now()}`,
      title: 'New Event',
      day: 'Every Monday',
      time: '7:00 PM',
      description: 'Description of the new event',
      active: true
    };
    setEvents([...events, newEvent]);
    setEditingEvent(events.length);
  };

  const handleDeleteEvent = (index) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      const updated = events.filter((_, i) => i !== index);
      setEvents(updated);
      if (editingEvent === index) {
        setEditingEvent(null);
      }
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading events...</div>;
  }

  return (
    <div className={styles['events-editor']}>
      <div className={styles['editor-header']}>
        <h2>Events Management</h2>
        <p>Manage What's On events that appear on the Welcoming Bar page.</p>
      </div>

      {message.text && (
        <div className={`${styles.message} ${styles[message.type]}`}>
          {message.text}
        </div>
      )}

      <div className={styles['events-list']}>
        {events.map((event, index) => (
          <div key={event.id} className={`${styles['event-card']} ${!event.active ? styles.inactive : ''}`}>
            <div className={styles['event-header']}>
              <div className={styles['event-status']}>
                <button
                  onClick={() => handleToggleActive(index)}
                  className={`${styles['status-toggle']} ${event.active ? styles.active : styles.inactive}`}
                >
                  {event.active ? '✓ Active' : '✗ Inactive'}
                </button>
              </div>
              <div className={styles['event-actions']}>
                <button
                  onClick={() => setEditingEvent(editingEvent === index ? null : index)}
                  className={styles['edit-button']}
                >
                  {editingEvent === index ? 'Done' : 'Edit'}
                </button>
                <button
                  onClick={() => handleDeleteEvent(index)}
                  className={styles['delete-button']}
                >
                  Delete
                </button>
              </div>
            </div>

            {editingEvent === index ? (
              <div className={styles['event-form']}>
                <div className={styles['form-row']}>
                  <label>Title:</label>
                  <input
                    type="text"
                    value={event.title}
                    onChange={(e) => handleEventUpdate(index, 'title', e.target.value)}
                    className={styles['form-input']}
                  />
                </div>
                <div className={styles['form-row']}>
                  <label>Day:</label>
                  <input
                    type="text"
                    value={event.day}
                    onChange={(e) => handleEventUpdate(index, 'day', e.target.value)}
                    className={styles['form-input']}
                    placeholder="e.g., Every Wednesday, First Friday"
                  />
                </div>
                <div className={styles['form-row']}>
                  <label>Time:</label>
                  <input
                    type="text"
                    value={event.time}
                    onChange={(e) => handleEventUpdate(index, 'time', e.target.value)}
                    className={styles['form-input']}
                    placeholder="e.g., 8:00 PM"
                  />
                </div>
                <div className={styles['form-row']}>
                  <label>Description:</label>
                  <textarea
                    value={event.description}
                    onChange={(e) => handleEventUpdate(index, 'description', e.target.value)}
                    className={styles['form-textarea']}
                    rows="3"
                  />
                </div>
              </div>
            ) : (
              <div className={styles['event-display']}>
                <h3>{event.title}</h3>
                <div className={styles['event-timing']}>
                  <span className={styles['event-day']}>{event.day}</span>
                  <span className={styles['event-time']}>{event.time}</span>
                </div>
                <p className={styles['event-description']}>{event.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={styles['add-event-section']}>
        <button
          onClick={handleAddEvent}
          className={styles['add-button']}
        >
          + Add New Event
        </button>
      </div>

      <div className={styles['editor-actions']}>
        <button
          onClick={handleReset}
          className={styles['reset-button']}
          disabled={saving}
        >
          Reset Changes
        </button>
        <button
          onClick={handleSave}
          className={styles['save-button']}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className={styles['help-text']}>
        <h4>Tips:</h4>
        <ul>
          <li>Use "Active" to show events on the website, "Inactive" to hide them</li>
          <li>Events appear in the "What's On" section of the Welcoming Bar page</li>
          <li>Use clear, descriptive titles and times</li>
          <li>Changes are saved locally until you deploy updates to the live site</li>
        </ul>
      </div>
    </div>
  );
};

export default EventsEditor;